import React, { useState } from 'react';
import type { FormikHelpers } from 'formik';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import TextFieldFormik, { DatePickerTextFieldFormik, MultiSelectTextFieldFormik } from '~/components/TextFieldFormik';
import { GeneratedReportListResponseGeneratedReportStatus } from '~/server_shared/openapi/data-contracts';

import { GENERATED_REPORTS_TEXTS } from './texts';

export interface FilterSearchValues {
  name: string;
  status: string[];
  requestedAtStart: string;
  requestedAtEnd: string;
}

interface GeneratedReportsFiltersProps {
  handleSearch: (values: FilterSearchValues) => void;
  clearFilters: () => void;
  isLoading: boolean;
}

const STATUS_OPTIONS = [
  { value: GeneratedReportListResponseGeneratedReportStatus.Initial, label: GENERATED_REPORTS_TEXTS.STATUS_INITIAL },
  {
    value: GeneratedReportListResponseGeneratedReportStatus.Processing,
    label: GENERATED_REPORTS_TEXTS.STATUS_PROCESSING,
  },
  {
    value: GeneratedReportListResponseGeneratedReportStatus.Completed,
    label: GENERATED_REPORTS_TEXTS.STATUS_COMPLETED,
  },
  { value: GeneratedReportListResponseGeneratedReportStatus.Failed, label: GENERATED_REPORTS_TEXTS.STATUS_FAILED },
];

const INITIAL_VALUES: FilterSearchValues = {
  name: '',
  status: [],
  requestedAtStart: '',
  requestedAtEnd: '',
};

const validationSchema = Yup.object().shape({
  name: Yup.string(),
  status: Yup.array().of(Yup.string()),
  requestedAtStart: Yup.string(),
  requestedAtEnd: Yup.string(),
});

const GeneratedReportsFilters: React.FC<GeneratedReportsFiltersProps> = ({ handleSearch, clearFilters, isLoading }) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  return (
    <div>
      <CollapsibleWrapper
        title={GENERATED_REPORTS_TEXTS.FILTER_TITLE}
        actionCard
        noBorder
        open={isSearchOpen}
        onCollapse={() => setIsSearchOpen((prev) => !prev)}
        unmountOnExit={false}
        isActionCardDrawer={false}
      >
        <Formik<FilterSearchValues>
          enableReinitialize
          initialValues={INITIAL_VALUES}
          validationSchema={validationSchema}
          onSubmit={(values: FilterSearchValues, { setSubmitting }: FormikHelpers<FilterSearchValues>) => {
            handleSearch(values);
            setSubmitting(false);
          }}
        >
          {({ handleSubmit, resetForm }) => (
            <div className="grid grid-cols-12 gap-24">
              <div className="col-span-12 sm:col-span-6 md:col-span-3">
                <TextFieldFormik
                  id="name"
                  label={GENERATED_REPORTS_TEXTS.FILTER_SEARCH_LABEL}
                  className="my-[3px]"
                  fullWidth
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-12 sm:col-span-6 md:col-span-2">
                <MultiSelectTextFieldFormik
                  id="status"
                  label={GENERATED_REPORTS_TEXTS.FILTER_STATUS_LABEL}
                  className="my-[3px]"
                  fullWidth
                  options={STATUS_OPTIONS.map((option) => option.value)}
                  renderValue={(selected: string[]) => {
                    if (selected.length === 0) {
                      return GENERATED_REPORTS_TEXTS.FILTER_STATUS_ALL;
                    }
                    return selected
                      .map((status) => STATUS_OPTIONS.find((opt) => opt.value === status)?.label || status)
                      .join(', ');
                  }}
                  renderOption={(status: string) => {
                    const option = STATUS_OPTIONS.find((opt) => opt.value === status);
                    return <span>{option?.label || status}</span>;
                  }}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-12 sm:col-span-6 md:col-span-2">
                <DatePickerTextFieldFormik
                  id="requestedAtStart"
                  label={GENERATED_REPORTS_TEXTS.FILTER_START_DATE}
                  className="my-[3px]"
                  disableFuture
                  fullWidth
                  clearable
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-12 sm:col-span-6 md:col-span-2">
                <DatePickerTextFieldFormik
                  id="requestedAtEnd"
                  label={GENERATED_REPORTS_TEXTS.FILTER_END_DATE}
                  className="my-[3px]"
                  disableFuture
                  fullWidth
                  clearable
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-12">
                <ButtonsContainer className="justify-start gap-12">
                  <Button
                    onClick={() => {
                      resetForm();
                      clearFilters();
                    }}
                    variant="contained"
                    className="border border-blue-600 bg-white text-blue-600 hover:shadow-sm"
                    disabled={isLoading}
                  >
                    {GENERATED_REPORTS_TEXTS.FILTER_CLEAR_BUTTON}
                  </Button>
                  <Button onClick={() => handleSubmit()} variant="contained" color="primary" disabled={isLoading}>
                    {GENERATED_REPORTS_TEXTS.FILTER_SEARCH_BUTTON}
                  </Button>
                </ButtonsContainer>
              </div>
            </div>
          )}
        </Formik>
      </CollapsibleWrapper>
    </div>
  );
};

export default GeneratedReportsFilters;
